import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

interface JobBasicData {
	title: string;
	description: string;
	placeOfPosting?: string;
	city: string[]; // Array of city names (comma-separated input)
	experienceLevel: string;
	tradeIds: string[]; // Array of trade UUIDs
	jobDeadline: string;
	country?: string; // Country for job posting
}

interface JobDetailsData {
	industry: string;
	employmentType: string;
	requiredSkills: string[];
	jobDeadline: string;
	otherIndustry?: string; // Field for "Others / Misc." industry
}

interface SalaryData {
	showPayBy: string;
	amount: string;
	minSalary: string;
	maxSalary: string;
	fixedSalary?: string;
	currency: string;
}

interface JobCreationState {
	jobBasic: JobBasicData | null;
	jobDetails: JobDetailsData | null;
	salaryData: SalaryData | null;
	currentStep: number;
	isLoading: boolean;
	error: string | null;
}

const initialState: JobCreationState = {
	jobBasic: null,
	jobDetails: null,
	salaryData: null,
	currentStep: 1,
	isLoading: false,
	error: null,
};

const jobCreationSlice = createSlice({
	name: 'jobCreation',
	initialState,
	reducers: {
		// Save job basic data
		saveJobBasic: (state, action: PayloadAction<JobBasicData>) => {
			state.jobBasic = action.payload;
			state.currentStep = 2;
			state.error = null;
		},
		
		// Save job details data
		saveJobDetails: (state, action: PayloadAction<JobDetailsData>) => {
			state.jobDetails = action.payload;
			state.currentStep = 3;
			state.error = null;
		},
		
		// Save salary data
		saveSalaryData: (state, action: PayloadAction<SalaryData>) => {
			state.salaryData = action.payload;
			state.error = null;
		},
		
		// Go back to previous step
		goBack: (state) => {
			if (state.currentStep > 1) {
				state.currentStep -= 1;
			}
		},
		
		// Set current step
		setCurrentStep: (state, action: PayloadAction<number>) => {
			state.currentStep = action.payload;
		},
		
		// Set loading state
		setLoading: (state, action: PayloadAction<boolean>) => {
			state.isLoading = action.payload;
		},
		
		// Set error
		setError: (state, action: PayloadAction<string | null>) => {
			state.error = action.payload;
		},
		
		// Clear all data (after successful job creation)
		clearJobCreationData: (state) => {
			state.jobBasic = null;
			state.jobDetails = null;
			state.salaryData = null;
			state.currentStep = 1;
			state.isLoading = false;
			state.error = null;
		},
		
		// Reset to initial state
		resetJobCreation: () => initialState,
	},
});

export const {
	saveJobBasic,
	saveJobDetails,
	saveSalaryData,
	goBack,
	setCurrentStep,
	setLoading,
	setError,
	clearJobCreationData,
	resetJobCreation,
} = jobCreationSlice.actions;

export default jobCreationSlice.reducer;
